import React, { useState, useEffect } from 'react';
import './Login.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const CIAM_URL = process.env.REACT_APP_CIAM_URL || 'https://config-dev.lrinternal.com';
const CIAM_API_KEY = process.env.REACT_APP_CIAM_API_KEY;

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isInvitedFlow = searchParams.get('invitation') === 'true';
  const invitationToken = searchParams.get('token');
  const invitationEmail = searchParams.get('email');

  const [formData, setFormData] = useState({
    email: isInvitedFlow ? invitationEmail : '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const [socialProviders, setSocialProviders] = useState([]);
  const [isLoadingProviders, setIsLoadingProviders] = useState(true);
  const [providerError, setProviderError] = useState(null);

  useEffect(() => {
    const fetchSocialProviders = async () => {
      try {
        const response = await fetch(
          `${CIAM_URL}/ciam/appInfo?apikey=${CIAM_API_KEY}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch social providers');
        }

        const data = await response.json();
        setSocialProviders(data.SocialSchema?.Providers || []);
      } catch (error) {
        console.error('Error fetching social providers:', error);
        setProviderError('Failed to load social login options');
      } finally {
        setIsLoadingProviders(false);
      }
    };

    fetchSocialProviders();
  }, []);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        `${CIAM_URL}/ciam/lookup?apikey=${CIAM_API_KEY}&email=${encodeURIComponent(formData.email)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to verify email');
      }

      const data = await response.json();

      if (!data.Provider) {
        // Show password field for regular login
        setShowPassword(true);
      } else {
        // Redirect to provider endpoint with the correct callback URL
        window.location.href = `${data.Provider.Endpoint}&callback=${window.location.origin}/sso/callback&callbacktype=querystring&is_access_token=true`;
      }
    } catch (error) {
      console.error('Email verification error:', error);
      setError(error.message || 'Failed to verify email');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const loginData = {
        email: formData.email,
        password: formData.password,
      };

      if (isInvitedFlow && invitationToken) {
        loginData.invitation_token = invitationToken;
      }

      const response = await fetch(`${API_URL}/api/v1/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
      });

      const data = await response.json();

      if (response.ok && data.access_token) {
        handleLoginSuccess(data);
      } else {
        throw new Error(data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLoginSuccess = async (data) => {
    try {
      // Store access token
      localStorage.setItem('accessToken', data.access_token);
      
      // Fetch profile and org data
      const profileResponse = await fetch(
        `${API_URL}/api/v1/auth/profile?access_token=${encodeURIComponent(data.access_token)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!profileResponse.ok) {
        throw new Error('Failed to fetch profile');
      }

      const profileData = await profileResponse.json();
      
      // Format and store profile data
      const formattedData = {
        name: profileData.Profile.FullName || `${profileData.Profile.FirstName} ${profileData.Profile.LastName}`.trim(),
        email: profileData.Profile.Email?.[0]?.Value || '',
        id: profileData.Profile.ID,
        uid: profileData.Profile.Uid,
        provider: profileData.Profile.Provider,
        Profile: profileData.Profile,
        Orgs: profileData.Orgs.map(org => ({
          OrgId: org.OrgId,
          Name: org.Name || org.OrgName,
          Permissions: org.Permissions,
          Policies: org.Policies || null
        }))
      };

      localStorage.setItem('profileData', JSON.stringify(formattedData));
      
      // Set default society if available
      if (formattedData.Orgs && formattedData.Orgs.length > 0) {
        const defaultSociety = formattedData.Orgs[0];
        localStorage.setItem('societyId', defaultSociety.OrgId);
        
        // Dispatch society changed event
        window.dispatchEvent(new Event('societyChanged'));
      }

      // Set authentication state
      login(data.access_token);

      // Small delay to ensure all data is set
      await new Promise(resolve => setTimeout(resolve, 100));

      // Navigate to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Error in login success:', error);
      setError('Failed to fetch user data');
      localStorage.removeItem('accessToken');
      login(null);
    }
  };

  // eslint-disable-next-line
  const handleSignupClick = () => {
    navigate('/signup');
  };

  const handleSocialLogin = (provider) => {
    const callbackUrl = `${window.location.origin}/sso/callback`;
    const endpoint = new URL(provider.Endpoint);
    
    // Add additional query parameters
    endpoint.searchParams.append('callbacktype', 'querystring');
    endpoint.searchParams.append('callback', callbackUrl);
    endpoint.searchParams.append('is_access_token', 'true');

    // Redirect to the social login endpoint
    window.location.href = endpoint.toString();
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-image-section">
          <div className="auth-image-overlay">
            <h1>Welcome Back</h1>
            <p>Access your B2B CRM platform</p>
          </div>
        </div>
        <div className="auth-form-section">
          <div className="auth-form-container">
            <h2>Login</h2>
            <p className="auth-subtitle">
              {isInvitedFlow 
                ? 'Complete your invitation by logging in' 
                : 'Sign in to your account'}
            </p>
            
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={showPassword ? handlePasswordSubmit : handleEmailSubmit}>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={showPassword || isInvitedFlow}
                  className={`form-control ${(showPassword || isInvitedFlow) ? 'disabled' : ''}`}
                  placeholder="Enter your email"
                />
              </div>

              {showPassword && (
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="form-control"
                    placeholder="Enter your password"
                  />
                </div>
              )}

              <button 
                type="submit" 
                className="auth-button"
                disabled={loading}
              >
                {loading ? 'Please wait...' : (showPassword ? 'Login' : 'Continue')}
              </button>
            </form>
            
            <div className="auth-footer">
              <p>Don't have an account? 
                <button 
                  onClick={() => navigate('/signup')} 
                  className="auth-link"
                >
                  Sign Up
                </button>
              </p>
            </div>

            {/* Social Login Section */}
            <div className="social-login-section">
              <div className="divider">
                <span>OR</span>
              </div>

              {isLoadingProviders ? (
                <div className="loading-providers">
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Loading login options...</span>
                </div>
              ) : providerError ? (
                <div className="provider-error">
                  {providerError}
                </div>
              ) : socialProviders.length > 0 ? (
                <div className="social-buttons">
                  {socialProviders.map((provider) => (
                    <button
                      key={provider.Name}
                      className={`social-login-button ${provider.Name.toLowerCase()}`}
                      onClick={() => handleSocialLogin(provider)}
                    >
                      <i className={`fab fa-${provider.Name.toLowerCase()}`}></i>
                      Continue with {provider.Name}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 